import { useState } from 'react';
import { FormFieldRow, FormFieldRowValue } from '@components/form-field-row';
import { IconAdd } from '@components/icons';
import { XS_VIEWPORT } from '@constants/viewport';
import { useWindowSize } from '@hooks/use-window-size';

import { FormFieldTableMobileModal } from './components/form-field-table-mobile-modal';
import { FormFieldTableProps, FormFieldTableRow } from './form-field-table.type';

export const FORM_FIELD_TABLE_TEST_ID = 'form-field-table';

const PLACEHOLDER_CUSTOM_VALUE: FormFieldRowValue = { type: 'text', name: '' };

const tid = (prefix: string, suffix: string) => `${prefix}-${suffix}`;

const countCollected = (rows: FormFieldTableRow[]) =>
  rows.reduce((acc, row) => (row.requirement === 'off' ? acc : acc + 1), 0);

export const FormFieldTable = ({
  rows,
  onRequirementChange,
  onCustomFieldSave,
  onCustomFieldDelete,
  onAddCustomField,
  disabled = false,
  tableHeaderLabel,
  collectedCountLabel,
  addCustomFieldLabel,
  requirementLabels,
  typeLabels,
  namePlaceholder,
  minPlaceholder,
  maxPlaceholder,
  saveLabel,
  cancelLabel,
  deleteConfirm,
  mobileModalCopy,
  modalZIndex,
  modalSurfaceClassName,
  dataTestId = FORM_FIELD_TABLE_TEST_ID,
}: FormFieldTableProps) => {
  const [mobileEditingRowId, setMobileEditingRowId] = useState<string | null>(null);
  const isMobileEditor = useWindowSize(XS_VIEWPORT);

  const mobileEditingRow = mobileEditingRowId
    ? rows.find((candidate) => candidate.id === mobileEditingRowId) ?? null
    : null;

  const handleAddCustomField = () => {
    const newRowId = onAddCustomField();
    if (isMobileEditor) setMobileEditingRowId(newRowId);
  };

  const collected = countCollected(rows);
  const total = rows.length;

  return (
    <div
      className='flex flex-col w-full gap-2 p-2 bg-primary border border-extra-soft rounded shadow-[0_2px_4px_rgba(0,0,0,0.1)]'
      data-testid={dataTestId}>
      <div
        className='flex w-full items-center justify-between h-[38px] px-3 bg-tertiary rounded-t-sm'
        data-testid={tid(dataTestId, 'header')}>
        <span className='text-subtitle-m text-primary'>{tableHeaderLabel}</span>
        <span className='text-body-xs text-secondary' data-testid={tid(dataTestId, 'collected-count')}>
          {collectedCountLabel({ collected, total })}
        </span>
      </div>

      <ul className='flex flex-col w-full'>
        {rows.map((row) => {
          const key = row.id;
          const rowTestId = tid(dataTestId, `row-${key}`);

          if (row.kind === 'locked') {
            return (
              <li key={key} className='w-full' data-testid={rowTestId}>
                <FormFieldRow
                  label={row.label}
                  value={PLACEHOLDER_CUSTOM_VALUE}
                  onSave={() => {
                    /* locked rows never fire onSave */
                  }}
                  requirement={row.requirement}
                  onRequirementChange={() => {
                    /* locked rows never change requirement */
                  }}
                  locked
                  disabled={disabled}
                  requirementLabels={requirementLabels}
                />
              </li>
            );
          }

          if (row.kind === 'preset') {
            return (
              <li key={key} className='w-full' data-testid={rowTestId}>
                <FormFieldRow
                  label={row.label}
                  value={PLACEHOLDER_CUSTOM_VALUE}
                  onSave={() => {
                    /* preset rows never fire onSave — they have no inline editor body */
                  }}
                  requirement={row.requirement}
                  onRequirementChange={(next) => onRequirementChange(row.id, next)}
                  onMobileEditClick={() => setMobileEditingRowId(row.id)}
                  isCustom={false}
                  disabled={disabled}
                  requirementLabels={requirementLabels}
                  typeLabels={typeLabels}
                  saveLabel={saveLabel}
                  cancelLabel={cancelLabel}
                />
              </li>
            );
          }

          const isNewRow = row.value.name.trim() === '';

          return (
            <li key={key} className='w-full' data-testid={rowTestId}>
              <FormFieldRow
                label={row.value.name === '' ? namePlaceholder : row.value.name}
                value={row.value}
                onSave={(next) => onCustomFieldSave(row.id, next)}
                onDelete={() => onCustomFieldDelete(row.id)}
                isNew={isNewRow}
                defaultExpanded={isNewRow}
                requirement={row.requirement}
                onRequirementChange={(next) => onRequirementChange(row.id, next)}
                onMobileEditClick={() => setMobileEditingRowId(row.id)}
                disabled={disabled}
                requirementLabels={requirementLabels}
                typeLabels={typeLabels}
                namePlaceholder={namePlaceholder}
                minPlaceholder={minPlaceholder}
                maxPlaceholder={maxPlaceholder}
                saveLabel={saveLabel}
                cancelLabel={cancelLabel}
                deleteConfirm={deleteConfirm}
                modalZIndex={modalZIndex}
                modalSurfaceClassName={modalSurfaceClassName}
              />
            </li>
          );
        })}
      </ul>

      <button
        type='button'
        onClick={handleAddCustomField}
        disabled={disabled}
        data-testid={tid(dataTestId, 'add-custom-field')}
        className='inline-flex w-full items-center justify-center gap-1 rounded bg-tertiary px-4 py-2 text-action-button-s text-primary disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary'>
        <IconAdd className='h-5 w-5 shrink-0' aria-hidden />
        <span>{addCustomFieldLabel}</span>
      </button>

      <FormFieldTableMobileModal
        row={mobileEditingRow}
        onClose={() => setMobileEditingRowId(null)}
        onRequirementChange={onRequirementChange}
        onCustomFieldSave={onCustomFieldSave}
        onCustomFieldDelete={onCustomFieldDelete}
        requirementLabels={requirementLabels}
        typeLabels={typeLabels}
        namePlaceholder={namePlaceholder}
        minPlaceholder={minPlaceholder}
        maxPlaceholder={maxPlaceholder}
        saveLabel={saveLabel}
        deleteConfirm={deleteConfirm}
        modalZIndex={modalZIndex}
        modalSurfaceClassName={modalSurfaceClassName}
        copy={mobileModalCopy}
        dataTestId={tid(dataTestId, 'mobile-modal')}
      />
    </div>
  );
};
